import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Edit, UserX, Send, ShieldCheck, ShieldOff, ArrowLeft, Search, Users, AlertCircle, Globe, Link } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAvailableMembers, getTeamMembers, inviteTeamMember, updateMemberAccess, removeTeamMember } from '../services/teamService';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../hooks/useLanguage';
import keycloak from '../utils/keycloakauth';
import axios from 'axios';
import { API_BASE_URL } from '../utils/config';

const BASE_URL = API_BASE_URL;

const TeamSettings = () => {
    const { keycloak: kc } = useKeycloak();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [currentLanguage, setUiLanguage] = useLanguage();
    const [members, setMembers] = useState([]);
    const [availableMembers, setAvailableMembers] = useState([]);
    const [tenantDomain, setTenantDomain] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
    const [error, setError] = useState(null);

    // State for invite form
    const [selectedMember, setSelectedMember] = useState('');
    const [inviteAccessLevel, setInviteAccessLevel] = useState('read');
    const [isInviting, setIsInviting] = useState(false);
    const [showMemberSelection, setShowMemberSelection] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Function to fetch team members
    const fetchMembers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getTeamMembers();
            setMembers(data || []); // Ensure members is always an array
        } catch (err) {
            setError(err.message);
            toast.error(t('teamSettings.errors.fetchFailed') + `: ${err.message}`);
            setMembers([]); // Reset members on error
        } finally {
            setIsLoading(false);
        }
    };

    // Function to fetch available members from the same tenant
    const fetchAvailableMembers = async () => {
        setIsLoadingAvailable(true);
        try {
            const data = await getAvailableMembers();
            setAvailableMembers(data.available_users || []);
            setTenantDomain(data.tenant_domain || '');
        } catch (err) {
            console.error('Failed to fetch available members:', err);
            toast.error(t('teamSettings.errors.fetchAvailableFailed', 'Failed to load available members') + `: ${err.message}`);
            setAvailableMembers([]);
        } finally {
            setIsLoadingAvailable(false);
        }
    };

    // Fetch members on component mount
    useEffect(() => {
        if (kc.authenticated) {
            fetchMembers();
            fetchAvailableMembers();
        }
    }, [kc.authenticated, t]);

    // Handle Invite Member
    const handleInviteMember = async (e) => {
        e.preventDefault();
        if (!selectedMember) {
            toast.error(t('teamSettings.errors.memberRequired', 'Please select a member to invite'));
            return;
        }
        setIsInviting(true);
        setError(null);
        try {
            await inviteTeamMember(selectedMember, inviteAccessLevel);
            toast.success(`${t('teamSettings.inviteSentTo')} ${selectedMember}`);
            setSelectedMember('');
            setSearchTerm('');
            setInviteAccessLevel('read');
            setShowMemberSelection(false);
            await fetchMembers(); // Refresh member list
            await fetchAvailableMembers(); // Refresh available members list
        } catch (err) {
             setError(err.message);
             toast.error(t('teamSettings.errors.inviteFailed') + `: ${err.message}`);
        } finally {
            setIsInviting(false);
        }
    };

    // Handle Update Access Level
    const handleUpdateAccess = async (memberId, newAccessLevel) => {
        try {
            await updateMemberAccess(memberId, newAccessLevel);
            toast.success(t('teamSettings.accessUpdated'));
            await fetchMembers(); // Refresh member list
        } catch (err) {
            toast.error(t('teamSettings.errors.updateFailed') + `: ${err.message}`);
        }
    };

    // Copy invite link to clipboard for pending invites
    const handleCopyInviteLink = async (memberId) => {
        try {
            const response = await axios.get(`${BASE_URL}/api/teams/invite-link/${memberId}`, {
                headers: { Authorization: `Bearer ${keycloak.token}` },
            });
            await navigator.clipboard.writeText(response.data.invite_link);
            toast.success(t('teamSettings.inviteLinkCopied'));
        } catch (err) {
            if (err.response?.data?.detail?.includes('auto-accepted')) {
                toast.success(t('teamSettings.memberAutoAccepted'));
            } else {
                toast.error(t('teamSettings.inviteLinkFailed', { message: err.response?.data?.detail || err.message }));
            }
        }
    };

    // Handle Remove Member
    const handleRemoveMember = async (memberId, memberEmail) => {
        if (!window.confirm(t('teamSettings.confirmRemove', { email: memberEmail }))) {
            return;
        }
        try {
            await removeTeamMember(memberId);
            toast.success(`${t('teamSettings.removedMember')} ${memberEmail}`);
            await fetchMembers(); // Refresh member list
            await fetchAvailableMembers(); // Refresh available members list
        } catch (err) {
             toast.error(t('teamSettings.errors.removeFailed') + `: ${err.message}`);
        }
    };

    // Helper to get status badge
    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'accepted':
                return <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">{t('teamSettings.statusAccepted')}</span>;
            case 'pending':
                return <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">{t('teamSettings.statusPending')}</span>;
            default:
                return null;
        }
    };

    // Helper to get access icon
     const getAccessIcon = (accessLevel) => {
        return accessLevel === 'write'
            ? <ShieldCheck size={16} className="text-blue-600 mr-1" />
            : <ShieldOff size={16} className="text-gray-500 mr-1" />;
    };

    // Filter available members based on search term
    const filteredAvailableMembers = availableMembers.filter(member =>
        member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.name && member.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // --- Render Logic ---
    return (
        <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl mx-auto mt-10">
            <div className="flex justify-between items-start mb-6">
                <div className="flex items-center">
                    <button 
                        onClick={() => navigate('/dashboard')} 
                        className="flex items-center mr-4 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        <span className="ml-1">{t('common.back')}</span>
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-800">{t('teamSettings.title')}</h2>
                </div>
                
                {/* Language Switcher */}
                <LanguageSwitcher
                    currentLanguage={currentLanguage}
                    onLanguageChange={setUiLanguage}
                />
            </div>

            {/* Domain info — subtle, not a warning */}
            {tenantDomain && (
                <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center text-sm text-blue-700">
                    <Users size={15} className="mr-2 flex-shrink-0" />
                    <span>{t('teamSettings.domainRestrictionPrefix')} <strong>@{tenantDomain}</strong></span>
                </div>
            )}

            {/* Invite Member Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-700">{t('teamSettings.inviteMember')}</h3>
                    <button
                        onClick={() => setShowMemberSelection(!showMemberSelection)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <Plus size={16} className="mr-2" />
                        {t('teamSettings.selectMember', 'Select Member')}
                    </button>
                </div>

                {showMemberSelection && (
                    <div className="space-y-4">
                        {/* Email input — type any address or pick from suggestions below */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="email"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setSelectedMember(e.target.value);
                                }}
                                placeholder={`Enter email, e.g. name@${tenantDomain || 'yourdomain.com'}`}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Suggestions from same-domain users already in the system */}
                        {filteredAvailableMembers.length > 0 && (
                            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white">
                                {isLoadingAvailable ? (
                                    <div className="p-4 text-center text-gray-500">{t('common.loading')}...</div>
                                ) : (
                                    filteredAvailableMembers.map((member) => (
                                        <div
                                            key={member.email}
                                            className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                                                selectedMember === member.email ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={() => {
                                                setSelectedMember(member.email);
                                                setSearchTerm(member.email);
                                            }}
                                        >
                                            <div className="font-medium text-gray-900 text-sm">{member.email}</div>
                                            {member.name && <div className="text-xs text-gray-500">{member.name}</div>}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Invite form — shown as soon as a valid email is typed/selected */}
                        {selectedMember && selectedMember.includes('@') && (
                            <form onSubmit={handleInviteMember} className="p-4 bg-white border border-gray-200 rounded-md">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm text-gray-700">
                                        {t('teamSettings.inviting', 'Inviting')}: <strong>{selectedMember}</strong>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedMember(''); setSearchTerm(''); }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="flex gap-3">
                                    <select
                                        value={inviteAccessLevel}
                                        onChange={(e) => setInviteAccessLevel(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={isInviting}
                                    >
                                        <option value="read">{t('teamSettings.accessRead')}</option>
                                        <option value="write">{t('teamSettings.accessWrite')}</option>
                                    </select>
                                    <button
                                        type="submit"
                                        className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                                        disabled={isInviting}
                                    >
                                        {isInviting ? (
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <Send size={16} className="mr-2" />
                                        )}
                                        {isInviting ? t('teamSettings.inviting') : t('teamSettings.sendInvite')}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>

            {/* Member List */}
            <h3 className="text-lg font-medium mb-4 text-gray-700">{t('teamSettings.members')}</h3>
            {isLoading && <p className="text-gray-500">{t('common.loading')}...</p>}
            {error && <p className="text-red-600 bg-red-50 p-3 rounded-md">{t('common.error')}: {error}</p>}
            {!isLoading && !error && (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {console.log('TeamSettings render - currentLanguage:', currentLanguage, 'i18n.language:', i18n.language, 'email translation:', t('teamSettings.email'))}
                                    {t('teamSettings.email')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('teamSettings.accessLevel')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('teamSettings.status')}</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('teamSettings.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {members.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">{t('teamSettings.noMembers')}</td>
                                </tr>
                            ) : (
                                members.map((member) => (
                                    <tr key={member.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{member.email}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center">
                                                {getAccessIcon(member.access_level)}
                                                <select
                                                    value={member.access_level}
                                                    onChange={(e) => handleUpdateAccess(member.id, e.target.value)}
                                                    className="ml-1 px-2 py-1 text-sm text-gray-900 border rounded"
                                                >
                                                    <option value="read">{t('teamSettings.accessRead')}</option>
                                                    <option value="write">{t('teamSettings.accessWrite')}</option>
                                                </select>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {getStatusBadge(member.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-3">
                                                {member.status === 'pending' && (
                                                    <button
                                                        onClick={() => handleCopyInviteLink(member.id)}
                                                        className="text-blue-500 hover:text-blue-700 transition-colors"
                                                        title={t('teamSettings.copyInviteLinkTooltip')}
                                                    >
                                                        <Link size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleRemoveMember(member.id, member.email)}
                                                    className="text-red-600 hover:text-red-800 transition-colors"
                                                    title={t('teamSettings.removeMember')}
                                                >
                                                    <UserX size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TeamSettings; 